export const getDefaultSelectStyles = (selectStyles = {}) => ({
  valueContainer: (base) => ({
    ...base,
    backgroundColor: 'white',
    border: 'none',
    borderRadius: '16px',
    minHeight: '56px',
    ...selectStyles.valueContainer,
  }),
  container: (base) => ({
    ...base,
    backgroundColor: 'white',
    border: 'none',
    borderRadius: '16px',
    width: '100%',
    // control's min-height (below, or via a size/bordered preset) is a
    // floor, not a hard cap — react-select's own internal content (value
    // text, indicators) can still need more room than that floor on some
    // rows. Without clipping here, control's own solid background would
    // paint over container's border-bottom when that happens. The menu is
    // portaled to document.body (see Dropdown.component.jsx's
    // menuPortalTarget), so it's never a child of this box — safe to clip
    // without ever hiding the open dropdown menu.
    overflow: 'hidden',
    ...selectStyles.container,
  }),
  control: (base, state) => ({
    ...base,
    backgroundColor: 'white',
    border: 'none',
    borderRadius: '16px',
    minHeight: '56px',
    // react-select's own base control style sets a focus boxShadow in its
    // default blue (~#2684FF) — since nothing here overrode `boxShadow`,
    // that default was passing straight through untouched. Replacing it
    // with an on-brand indigo ring instead of silently inheriting it.
    boxShadow: state.isFocused ? '0 0 0 3px rgba(68, 76, 231, 0.15)' : 'none',
    ...selectStyles.control,
  }),
  indicatorSeparator: () => {
    return { display: 'none' };
  },
  singleValue: (base) => ({
    ...base,
    paddingLeft: '16px',
    fontSize: 14,
    fontWeight: 400,
    ...selectStyles.singleValue,
  }),
  input: (base) => ({
    ...base,
    paddingLeft: '16px',
    ...selectStyles.input,
  }),
  menu: (base) => ({
    ...base,
    margin: 0,
    width: '100%',
    backgroundColor: '#FAFAFA',
    borderRadius: '0 0 16px 16px',
    overflow: 'hidden',
    boxShadow: 'none',
    border: 'none',
    marginTop: '1px !important',
    zIndex: 10000,
    ...selectStyles.menu,
  }),
  menuList: (base) => ({
    ...base,
    borderRadius: '0 0 16px 16px',
    boxShadow: 'none',
    border: 'none',
    '::-webkit-scrollbar': {
      width: '6px',
      height: '0px',
    },
    '::-webkit-scrollbar-track': {
      background: '#FAFAFA',
    },
    '::-webkit-scrollbar-thumb': {
      background: '#156CD7',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
    ...selectStyles.menuList,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10000,
    ...selectStyles.menuPortal,
  }),
  option: (styles, state) => {
    return {
      ...styles,
      '&:hover': {
        cursor: 'pointer',
        color: '#174091',
        backgroundColor: '#EEF7FF',
      },
      color: state.isSelected ? '#174091' : '#3A3F52',
      fontWeight: 400,
      fontSize: 14,
      backgroundColor: state.isSelected ? '#EEF7FF' : '#FFF',
      ...selectStyles.option,
    };
  },
  placeholder: (base) => ({
    ...base,
    paddingLeft: '16px',
    fontSize: 14,
    color: '#bfbfbf',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    ...selectStyles.placeholder,
  }),
  group: (base) => ({
    ...base,
    borderBottom: '1px solid #E0E4EA',
    ...selectStyles.group,
  }),
  groupHeading: (base) => ({
    ...base,
    fontSize: 12,
    fontWeight: 400,
    color: 'white',
    padding: '4px 4px 4px 12px',
    backgroundColor: '#3B82F6',
    ...selectStyles.groupHeading,
  }),
});

export const dropdownSizes = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
};

// `totalHeight` is the height a caller actually sees and compares against a
// same-height sibling (e.g. a Button) — the outer bordered pill's full
// visible footprint. It is NOT `control`'s own height: the border is drawn
// on `container`, a separate box wrapping `control` with no explicit size
// of its own, so it just adds its border on top of whatever `control`
// renders at rather than being absorbed inside a fixed box. `control` has
// to be sized smaller than the target by the border's width on each side,
// or the total ends up 2px taller than `totalHeight` says.
const BORDER_WIDTH_PX = 1;

const SIZE_CONFIG = {
  [dropdownSizes.sm]: { totalHeight: 32, borderRadius: '8px' },
  [dropdownSizes.md]: { totalHeight: 42, borderRadius: '16px' },
  [dropdownSizes.lg]: {
    totalHeight: 48,
    borderRadius: '10px',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
  },
};

// A visible border always goes on `container`, never `control` — the base
// styles above set `control.border: 'none'` unconditionally, so a border
// placed there would just be discarded. `control`/`valueContainer` share
// the same derived (border-adjusted) height. This is the single source
// both the legacy named presets below and Dropdown's own `size`/`bordered`
// props are built from — a new size is one SIZE_CONFIG entry, not a
// hand-copied object.
export const makeBorderedSelectStyles = (size = dropdownSizes.md) => {
  const { totalHeight, borderRadius, boxShadow } = SIZE_CONFIG[size];
  const controlHeight = `${totalHeight - BORDER_WIDTH_PX * 2}px`;
  return {
    container: {
      border: `${BORDER_WIDTH_PX}px solid var(--gray-200)`,
      borderRadius,
      ...(boxShadow && { boxShadow }),
    },
    control: { minHeight: controlHeight, borderRadius },
    valueContainer: { minHeight: controlHeight },
  };
};

// Legacy named presets — kept because existing call sites (~26 view files)
// import these directly. Prefer Dropdown's own `size`/`bordered` props for
// new call sites instead of reaching for one of these by name.
export const borderedSelectStyles = {
  ...makeBorderedSelectStyles(dropdownSizes.md),
  option: { backgroundColor: '#fafafa' },
};

export const filterBarSelectStyles = makeBorderedSelectStyles(dropdownSizes.lg);
